import { parseBoolean, parseRecord, parseString } from "@de-xima/xima-common-js-lang";
import Framebus from "framebus";

import { FrameBusChildHandler, FrameBusTopHandler } from "../core/FramebusHandler.ts";
import { invokeAllDeferredRenders, readUploadFile, remoteCommandParams, runRemoteCommand } from "../core/Util.ts";

/**
 * The {@link FrameBusTopHandler} for the designer backend.
 */
export class DesignerFrameBusTopHandler extends FrameBusTopHandler {
  private _framebus: Framebus;

  /**
   * Stores the latest value that was sent to the `changesStatus` remote
   * command, for each page ID. The remote command is invoked only when the
   * status changes to prevent needless AJAX requests.
   */
  private _latestChangesStatus: Record<string, boolean | undefined> = {};

  constructor() {
    super();
    this._framebus = new Framebus({
      channel: "designerChannel",
      origin: window.location.origin,
    });
    this._init();
  }

  /**
   * Scrolls to the given form element indicated by the ID and selects it in the
   * form designer.
   * @param elementId ID of the form element to which to scroll.
   */
  emitScrollToFormItemById(elementId: string): void {
    this._framebus.emit("scroll-to-form-item-by-id", {
      id: elementId,
    });
  }

  /**
   * When the user switched to a different tab.
   * @param newTabId ID of the new tab.
   */
  emitSwitchedTab(pageId: string): void {
    this._framebus.emit("switchedTab", {
      pageId: pageId,
    });
  }

  /**
   * Publishes an xm action, i.e. a form designer button action. Used when the
   * toolbar is inside another frame as the designer.
   * @param actionName Name of the xm action to publish.
   * @param payload Optional payload to send with the action.
   */
  emitXmAction(actionName: string, payload: unknown = {}): void {
    this._framebus.emit("xm-action", {
      actionName: actionName,
      payload: payload,
    });
  }

  /**
   * Open a file choose dialog, lets the user select a persist file, and finally
   * passes the data through to the form designer.
   */
  async emitXmLoad(): Promise<void> {
    try {
      const persistFile = await readUploadFile(".json,.xdp,.fcp");
      if (persistFile !== undefined) {
        this.emitXmAction("xm-load", {
          persistFile,
          success: true,
        });
      }
    } catch (e) {
      console.error("Could not read selected persist file", e);
      this.emitXmAction("xm-load", {
        persistFile: undefined,
        success: false,
      });
    }
  }

  override getFramebus(): Framebus {
    return this._framebus;
  }

  protected override _init(): void {
    super._init();

    // Forward errors that occurred in a tab to the bean handler
    this._framebus.on("pageError", (data) => {
      this._handlePageError(data).catch((e) => console.error("Could not process page error event", e));
    });

    // Show whether the tab has got unsaved changes
    this._framebus.on("changesStatus", (data) => {
      this._handleChangesStatus(data).catch((e) => console.error("Could not handle change status event", e));
    });

    // Open the dialog for adding a new state to the workflow
    this._framebus.on("addNewStateDialog", () => {
      BackendCommand?.Designer?.openAddNewState?.().catch((e) => console.error("Could not add new state", e));
    });
  }

  /**
   * Informs the bean that the tab has got unsaved changes or that all changes
   * were saved.
   * @param data Data received from the event about the new status.
   */
  private async _handleChangesStatus(data: unknown): Promise<void> {
    const record = parseRecord(data);
    const pageId = parseString(record.pageId);
    const hasUnsavedChanges = parseBoolean(record.hasUnsavedChanges);
    try {
      if (hasUnsavedChanges !== this._latestChangesStatus[pageId]) {
        this._latestChangesStatus[pageId] = hasUnsavedChanges;
        await runRemoteCommand(BackendCommand?.Designer?.onChangesStatus, [
          { name: "hasUnsavedChanges", value: hasUnsavedChanges },
          { name: "pageId", value: pageId },
        ]);
      }
    } catch (e) {
      console.error("Could not send changes status", e);
    }
  }

  /**
   * Inform the bean that an error occurred in the tab that resulted in the tab
   * not functioning propertly anymore.
   * @param data Error data received from the event.
   */
  private async _handlePageError(data: unknown): Promise<void> {
    const errorData = parseRecord(data);
    const pageId = parseString(errorData.pageId);
    const errorMessage = parseString(errorData.errorMessage);
    try {
      await runRemoteCommand(BackendCommand?.Designer?.onPageError, [
        { name: "errorMessage", value: errorMessage },
        { name: "pageId", value: pageId },
      ]);
    } catch (e) {
      console.error("Could not send process tab error", e);
    }
  }

  static createInstance(): DesignerFrameBusTopHandler {
    return new DesignerFrameBusTopHandler();
  }
}

/**
 * Base {@link FrameBusChildHandler} for IFRAME pages inside the designer, such
 * as the form and workflow designer, but also several backend dialogs such as
 * the client files dialog.
 */
export abstract class DesignerFrameBusChildHandler extends FrameBusChildHandler {
  /**
   * Opens the dialog for adding a new state.
   */
  emitAddNewStateDialog(): void {
    this.getFramebus().emit("addNewStateDialog");
  }

  /**
   * @param hasUnsavedChanges Whether the tab has got unsaved changes.
   */
  emitChangesStatus(hasUnsavedChanges: boolean): void {
    this.getFramebus().emit("changesStatus", {
      hasUnsavedChanges,
      pageId: this.getPageId(),
    });
  }

  /**
   * Publishes a form designer preview action. Opens a new window and delegates
   * to the designer frame.
   */
  emitFormPreview(): void {
    this.getFramebus().emit("formPreview");
  }

  /**
   * Switch to another designer tab with the given pageId
   * @param pageId of the designer tab
   * @param preselect Optional ID or UUID of the entity to preselect in the tab.
   */
  emitSwitchTab(pageId: string, preselect?: { id: string } | { uuid: string }): void {
    const params = remoteCommandParams<["Designer", "switchToTab"]>();
    params.push({ name: "pageId", value: pageId });
    if (preselect !== undefined && "id" in preselect) {
      params.push({ name: "preselect", value: preselect.id });
    } else if (preselect !== undefined && "uuid" in preselect) {
      params.push({ name: "preselectUuid", value: preselect.uuid });
    }
    this.emitRemoteCommandSafe(params, "BackendCommand", "Designer", "switchToTab");
  }

  protected override _init(): void {
    super._init();
    // Invoke deferred renders on tab change, as widgets won't notice otherwise
    this.getFramebus().on("switchedTab", (data) => {
      const pageId = parseString(data.pageId);
      if (pageId === this.getPageId()) {
        requestAnimationFrame(() => invokeAllDeferredRenders());
      }
    });
  }
}

export class GenericDesignerFrameBusTopHandler extends FrameBusTopHandler {
  private readonly _framebus: Framebus;

  private readonly _pageId: string;

  constructor(pageId: string, socketChannel: string) {
    super();
    this._framebus = new Framebus({
      channel: socketChannel,
      origin: document.getElementsByClassName("xm-frame-bus-origin").item(0)?.textContent ?? "*",
    });
    this._pageId = pageId;
    this._init();
  }

  /**
   * @returns The framebus instance
   */
  getFramebus(): Framebus {
    return this._framebus;
  }

  getPageId(): string {
    return this._pageId;
  }

  static createInstance(pageId: string, channel: string): GenericDesignerFrameBusTopHandler {
    return new GenericDesignerFrameBusTopHandler(pageId, channel);
  }
}

export class GenericDesignerFrameBusChildHandler extends DesignerFrameBusChildHandler {
  private readonly _framebus: Framebus;

  private readonly _pageId: string;

  constructor(pageId: string, socketChannel: string) {
    super();
    this._framebus = new Framebus({
      channel: socketChannel,
      origin: document.getElementsByClassName("xm-frame-bus-origin").item(0)?.textContent ?? "*",
    });
    this._pageId = pageId;
    this._init();
  }

  /**
   * @returns The framebus instance
   */
  getFramebus(): Framebus {
    return this._framebus;
  }

  getPageId(): string {
    return this._pageId;
  }

  static createInstance(pageId: string, channel: string): GenericDesignerFrameBusChildHandler {
    return new GenericDesignerFrameBusChildHandler(pageId, channel);
  }
}
